import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { NgxCurrencyDirective } from 'ngx-currency';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { map, catchError, of, Subscription } from 'rxjs';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { SelectPesquisavelService } from '@shared/components/select-pesquisavel/select-pesquisavel.service';
import {
    PesquisaCodigoTributacaoModalComponent,
    PesquisaCodigoTributacaoModalData,
    PesquisaCodigoTributacaoModalResult,
} from '@shared/components/pesquisa-codigo-tributacao-modal/pesquisa-codigo-tributacao-modal.component';
import { MaskService } from '@shared/services/mask.service';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { CstIcmsStruct } from '@shared/services/structs/motor-fiscal/cst-icms.struct';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { LookupItemStruct } from '@shared/services/structs/motor-fiscal/lookup-item.struct';
import { getCstIcmsTexto, CstIcmsTexto, getMotivosDesoneracaoSugeridos } from '@shared/constants/motor-fiscal/cst-icms-metadata';
import {
    IndicadoresCst,
    MapaCamposIndicador,
    isAlgumCampoAtivo,
    isCampoAtivo,
    isCodigoDaOperacao,
    lerIndicadoresCst,
    limparCamposInativos,
} from '@shared/constants/motor-fiscal/indicadores-campos.util';
import { ModalidadeBcOption } from '@shared/services/structs/motor-fiscal/modalidade-bc-option.struct';

/** Campo do indicador do CST do ICMS -> control da regra da natureza (aba e wizard de exceção usam os mesmos nomes). */
export const MAPA_CAMPOS_ICMS_NATUREZA: MapaCamposIndicador = {
    modBC: 'modalidadeBc',
    pRedBC: 'pRedBc',
    pICMS: 'pIcms',
    pFCP: 'pFcp',
    modBCST: 'modalidadeBcSt',
    pMVAST: 'pMvaSt',
    pRedBCST: 'pRedBcSt',
    pICMSST: 'pIcmsSt',
    pFCPST: 'pFcpSt',
    pDif: 'pDif',
    pFCPDif: 'pFcpDif',
    cBenefRBC: 'codigoBeneficioRbc',
    pRedAdRem: 'pRedAdRem',
    motRedAdRem: 'motRedAdRem',
    motDesICMS: 'motDesIcms',
    motDesICMSST: 'motDesIcmsSt',
    pST: 'pSt',
    pFCPSTRet: 'pFcpStRet',
    pRedBCEfet: 'pRedBcEfet',
    pICMSEfet: 'pIcmsEfet',
    obterStCompra: 'obterIcmsStCompra',
};

@Component({
    selector: 'app-icms-tributo-regra-section',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatButtonModule,
        MatBadgeModule,
        MatIconModule,
        MatTooltipModule,
        NgxCurrencyDirective,
        NgxMaskDirective,
        AlertCardComponent,
    ],
    providers: [provideNgxMask()],
    templateUrl: './tributo-regra-section.component.html',
    styleUrl: './tributo-regra-section.component.css',
})
export class IcmsTributoRegraSectionComponent implements OnChanges, OnDestroy {
    @Input({ required: true }) form!: FormGroup;
    @Input() codigoTributos: CstIcmsStruct[] = [];
    @Input() cfopLookupUrl = '';
    @Input() variaveis: VariavelStruct[] = [];
    @Input() personalizadasCount = 0;
    @Input() modoExcecao = false;
    @Input() personalizarDisabled = false;
    @Input() cfopControlName = 'cfopPadrao';
    /** Tipo de operação da natureza: Entrada ou Saida. Define os dígitos do CFOP no tooltip. */
    @Input() tipoOperacao: TipoOperacaoEnum = TipoOperacaoEnum.Saida;
    @Output() personalizar = new EventEmitter<void>();

    private cstSubscription?: Subscription;
    public readonly percentMask = this.maskService.getPercentMask();
    public readonly currencyMask = this.maskService.getCurrencyMask();
    public readonly masks = MaskService.getMasks();
    /** Permite o coringa "?" na 1ª posição do CFOP (a regra por posição é garantida no onCfopKeydown). */
    public readonly cfopPatterns = { '0': { pattern: /[0-9?]/ } };

    public readonly cfopEntradaTooltipInfo =
        'Use "?" no início do CFOP\n' +
        'para substituição automática\n' +
        'conforme a UF do fornecedor.\n\n' +
        'Exemplo: Para o CFOP ?102,\n' +
        'o "?" será substituído por:\n\n' +
        '1 - Operação dentro da mesma UF\n' +
        '2 - Operação interestadual\n' +
        '3 - Operação de importação';

    public readonly cfopSaidaTooltipInfo =
        'Use "?" no início do CFOP\n' +
        'para substituição automática\n' +
        'conforme a UF do cliente.\n\n' +
        'Exemplo: Para o CFOP ?102,\n' +
        'o "?" será substituído por:\n\n' +
        '5 - Operação dentro da mesma UF\n' +
        '6 - Operação interestadual\n' +
        '7 - Operação de exportação';

    /** Tooltip do "?" conforme o tipo de operação (entrada usa 1/2/3, saída usa 5/6/7). */
    public get cfopTooltipInfo(): string {
        return this.tipoOperacao === TipoOperacaoEnum.Entrada ? this.cfopEntradaTooltipInfo : this.cfopSaidaTooltipInfo;
    }

    /** Dígitos válidos para a 1ª posição do CFOP conforme o tipo de operação. */
    public get cfopPrimeirosDigitos(): string[] {
        return this.tipoOperacao === TipoOperacaoEnum.Entrada ? ['1', '2', '3'] : ['5', '6', '7'];
    }

    public get cfopPlaceholder(): string {
        return this.tipoOperacao === TipoOperacaoEnum.Entrada ? 'Ex: 1102, ?102, ?101' : 'Ex: 5102, ?102, ?101';
    }

    @Input() modalidadesBc: ModalidadeBcOption[] = [];
    @Input() modalidadesBcSt: ModalidadeBcOption[] = [];
    @Input() motivosDesoneracao: LookupItemStruct[] = [];
    @Input() motivosReducaoAdRem: LookupItemStruct[] = [];

    /** Rótulo do toggle DIFAL, independente do CST — muda só pela direção da natureza. */
    public get difalLabel(): string {
        return this.tipoOperacao === TipoOperacaoEnum.Entrada
            ? 'Considerar DIFAL/antecipação na entrada'
            : 'Habilitar DIFAL da operação interestadual';
    }

    /** Aviso não impeditivo: o motivo escolhido não está entre os sugeridos pelo leiaute para este CST (próprio). */
    public get avisoMotivoDesoneracaoForaSugerido(): boolean {
        const cst = this.form.get('cstIcms')?.value ?? null;
        const motivo = this.form.get('motDesIcms')?.value ?? null;
        if (!motivo) return false;
        const sugeridos = getMotivosDesoneracaoSugeridos(cst)?.proprio;
        return !!sugeridos && !sugeridos.includes(motivo);
    }

    /** Idem, para o motivo de desoneração do ICMS-ST. */
    public get avisoMotivoDesoneracaoStForaSugerido(): boolean {
        const cst = this.form.get('cstIcms')?.value ?? null;
        const motivo = this.form.get('motDesIcmsSt')?.value ?? null;
        if (!motivo) return false;
        const sugeridos = getMotivosDesoneracaoSugeridos(cst)?.st;
        return !!sugeridos && !sugeridos.includes(motivo);
    }

    constructor(
        private maskService: MaskService,
        private dialog: MatDialog,
        private selectPesquisavelService: SelectPesquisavelService,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['form'] && this.form) {
            this.cstSubscription?.unsubscribe();
            // Zera os campos que o novo CST não exibe (evita valor obsoleto no payload).
            this.cstSubscription = this.form.get('cstIcms')?.valueChanges.subscribe(() =>
                limparCamposInativos(this.form, this.indicadores, MAPA_CAMPOS_ICMS_NATUREZA));
        }
    }

    ngOnDestroy(): void {
        this.cstSubscription?.unsubscribe();
    }

    /** Calculados pelo CST atual do form: ao restaurar o CST anterior (cancelar a troca), a tela volta junto. */
    public get indicadores(): IndicadoresCst {
        const cst = this.form?.get('cstIcms')?.value ?? null;
        return lerIndicadoresCst(this.codigoTributos.find(c => c.codigo === cst)?.indicadores);
    }

    public get cstIcmsTexto(): CstIcmsTexto | null {
        return getCstIcmsTexto(this.form?.get('cstIcms')?.value ?? null);
    }

    /** Só os CSTs da operação (entrada ou saída) da natureza, pelo indicador. */
    public get codigoTributosDaOperacao(): CstIcmsStruct[] {
        return this.codigoTributos.filter(c => isCodigoDaOperacao(c.indicadores, this.tipoOperacao));
    }

    public ativo(chave: string): boolean {
        return isCampoAtivo(this.indicadores, chave);
    }

    public algumAtivo(chaves: string[]): boolean {
        return isAlgumCampoAtivo(this.indicadores, chaves);
    }

    public get cfopControl(): FormControl {
        return this.form.get(this.cfopControlName) as FormControl;
    }

    /** True quando o botão "Personalizar fórmulas" não pode ser acessado. */
    public get personalizarBloqueado(): boolean {
        return this.personalizarDisabled || !this.form?.get('cstIcms')?.value;
    }

    /** Mensagem do tooltip exibida apenas quando o botão está desabilitado. */
    public get personalizarTooltip(): string {
        if (this.personalizarDisabled) {
            return this.modoExcecao
                ? 'A personalização de fórmulas não está disponível para exceções.'
                : 'A personalização de fórmulas está indisponível no momento.';
        }
        if (!this.form?.get('cstIcms')?.value) {
            return 'Selecione um CST para habilitar a personalização de fórmulas.';
        }
        return '';
    }

    public onPersonalizar(): void {
        this.personalizar.emit();
    }

    /**
     * Valida entrada no campo CFOP. Na primeira posição aceita "?" ou apenas os dígitos válidos
     * do tipo de operação (entrada: 1/2/3; saída: 5/6/7); nas demais posições aceita qualquer
     * dígito. O tamanho (4 dígitos) e o ponto da máscara são controlados pelo ngx-mask.
     */
    public onCfopKeydown(event: KeyboardEvent): void {
        const input = event.target as HTMLInputElement;
        const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
        const isShortcut = (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
        if (allowed.includes(event.key) || isShortcut) return;

        const selStart = input.selectionStart ?? 0;

        // Primeira posição: aceita "?" ou apenas os dígitos válidos do tipo de operação.
        if (selStart === 0) {
            if (event.key === '?' || this.cfopPrimeirosDigitos.includes(event.key)) return;
            event.preventDefault();
            return;
        }

        // Demais posições: somente dígitos.
        if (!/^\d$/.test(event.key)) { event.preventDefault(); }
    }

    public pesquisarCfop(): void {
        const cfopUrl = `${this.cfopLookupUrl}?idTipoOperacao=${this.tipoOperacao}`;
        const dialogRef = this.dialog.open(PesquisaCodigoTributacaoModalComponent, {
            width: '700px',
            maxHeight: '85vh',
            panelClass: 'pesquisa-codigo-tributacao-dialog',
            data: {
                titulo: 'Pesquisar CFOP',
                buscarFn: (pesquisa: string) =>
                    this.selectPesquisavelService.getSelectValues<any>(cfopUrl, pesquisa).pipe(
                        map(list => ({
                            isError: false,
                            itens: list.map((i: any) => ({ codigo: String(i.codigo ?? ''), descricao: i.descricao })),
                        })),
                        catchError(() => of({ isError: true, itens: [] })),
                    ),
                permitirManual: false,
                registrosIniciais: 20,
                valorInicial: this.cfopControl?.value ?? undefined,
            } as PesquisaCodigoTributacaoModalData,
        });

        dialogRef.afterClosed().subscribe((result: PesquisaCodigoTributacaoModalResult | null) => {
            if (!result?.selecionado) return;
            this.cfopControl?.setValue(result.selecionado.codigo.replace(/\D/g, ''));
        });
    }
}
